import React, { useState, useEffect } from 'react';
import { QrCode, Copy, Check, X, Clock, Shield } from 'lucide-react';
import { VerificationService, VerificationToken } from '../../services/verification.service';
import { Button } from '../shared';

interface QRGeneratorProps {
    ticketId: string;
    userId: string;
    ticketNumber: string;
    onClose: () => void;
}

export const QRGenerator: React.FC<QRGeneratorProps> = ({
    ticketId,
    userId,
    ticketNumber,
    onClose
}) => {
    const [loading, setLoading] = useState(true);
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [pin, setPin] = useState<string>('');
    const [expiresAt, setExpiresAt] = useState<Date | null>(null);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        generateQR();
    }, []);

    const generateQR = async () => {
        setLoading(true);
        setError(null);

        const result = await VerificationService.generateVerificationToken(ticketId, userId);

        if (result) {
            setQrCodeUrl(result.qrCodeDataUrl);
            setPin(result.token.pin);
            setExpiresAt(result.token.expiresAt);
        } else {
            setError('Error al generar el código QR');
        }

        setLoading(false);
    };

    const copyPIN = () => {
        navigator.clipboard.writeText(pin);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatExpiration = (date: Date) => {
        const now = new Date();
        const diff = date.getTime() - now.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        return `${hours}h restantes`;
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
                    <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mb-4"></div>
                        <p className="text-gray-600">Generando código QR...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
                    <div className="text-center">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <X size={24} className="text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Error</h3>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <Button onClick={onClose} variant="secondary">
                            Cerrar
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
                {/* Header */}
                <div className="border-b p-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">
                                Código de Cierre
                            </h2>
                            <p className="text-sm text-gray-500">
                                Ticket #{ticketNumber}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* QR Code */}
                    <div className="flex justify-center">
                        <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-sm">
                            {qrCodeUrl && (
                                <img
                                    src={qrCodeUrl}
                                    alt="QR Code"
                                    className="w-64 h-64"
                                />
                            )}
                        </div>
                    </div>

                    {/* PIN Display */}
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Shield className="text-blue-600" size={20} />
                                <span className="text-sm font-medium text-blue-900">
                                    PIN de Verificación
                                </span>
                            </div>
                            <button
                                onClick={copyPIN}
                                className="text-blue-600 hover:text-blue-700"
                            >
                                {copied ? (
                                    <Check size={16} />
                                ) : (
                                    <Copy size={16} />
                                )}
                            </button>
                        </div>
                        <div className="flex items-center justify-center gap-2 py-4">
                            {pin.split('').map((digit, index) => (
                                <div
                                    key={index}
                                    className="w-12 h-16 bg-white border-2 border-blue-300 rounded-lg flex items-center justify-center text-3xl font-bold text-blue-900"
                                >
                                    {digit}
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-blue-700 text-center">
                            El técnico necesitará este PIN para cerrar el ticket
                        </p>
                    </div>

                    {/* Expiration */}
                    {expiresAt && (
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                            <Clock size={16} />
                            <span>Válido por {formatExpiration(expiresAt)}</span>
                        </div>
                    )}

                    {/* Instructions */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <h4 className="font-medium text-gray-900 text-sm">
                            Instrucciones:
                        </h4>
                        <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                            <li>Muestra este código QR al técnico</li>
                            <li>Proporciona el PIN de 4 dígitos</li>
                            <li>El técnico escaneará el QR e ingresará el PIN</li>
                            <li>El ticket se cerrará automáticamente</li>
                        </ol>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t p-4 bg-gray-50 rounded-b-lg">
                    <Button
                        onClick={onClose}
                        variant="secondary"
                        className="w-full"
                    >
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
};
